import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { W_BillingCode_UpdateBillingCode_RM } from 'ab-api/web/billingCode/editBillingCodes';
import type { W_BillingCode_FindForJob_VM_Single } from 'ab-api/web/billingCode/findForJob';

import type { BillingCodeVM } from 'ab-viewModels/billingCode.viewModel';
import type { WorkSummaryDefinitionFieldVM } from 'ab-viewModels/fieldReport/workSummary.viewModel';
import type WorkSummaryVM from 'ab-viewModels/fieldReport/workSummary.viewModel';
import type SiblingSubjobVM from 'ab-viewModels/workRequest/siblingSubjob.viewModel';

import { isAllowed } from '@acceligentllc/shared/utils/auth';
import { resolveToLargerUnit } from '@acceligentllc/shared/utils/compoundUnit';
import { convertUnits } from '@acceligentllc/shared/utils/unitConversion';

import type { ColorPalette } from '@acceligentllc/shared/enums/color';
import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';
import { QuantityUnitMap } from '@acceligentllc/shared/enums/quantityUnit';
import * as ReportBlockFieldEnum from '@acceligentllc/shared/enums/reportBlockField';
import { UnitClasses } from '@acceligentllc/shared/enums/unitConversion';

import type { RootState } from 'af-reducers';

import { bemBlock, bemElement } from 'af-utils/bem.util';
import { dollarFormatter } from 'af-utils/format.util';
import { useToggle, type CellWidthsMap } from 'af-utils/react.util';

import PagePermissions from 'ab-enums/pagePermissions.enum';
import WorkSummaryBillingType from 'ab-enums/workSummaryBillingType.enum';

import ColorSquare from 'af-components/ColorSquare';
import Dropdown from 'af-components/Controls/Dropdown';
import MaterialRateSource from 'af-components/MaterialRateSource';
import Tooltip from 'af-components/Tooltip';

import * as WorkRequestActions from 'af-actions/workRequests';

import ConvertedMaterialCost from './ConvertedMaterialCost';
import type { BackendBillingCodeErrors } from './EditBillingCodesModal';
import EditBillingCodesModal from './EditBillingCodesModal';
import MaterialCostDropdown from './MaterialCostDropdown';
import styles from './styles.module.scss';
import { resolveHighlightGroupKey } from '../../helpers';
import { resolveFieldValue } from '../../Table/helpers';
import { WORK_SUMMARY_COLUMN_DEFINITION_MAP, createIndexedColumn, resolveStyleForColumn } from '../../values';

interface OwnProps {
	rowData: WorkSummaryVM;
	billingCodes: W_BillingCode_FindForJob_VM_Single[];
	maxNumberOfDefinitionFields: number;
	isBillableStatusEditable: boolean;
	isReadOnly: boolean;
	onRowHighlight: (groupToHighlight: Nullable<string>, groupKeyToHighlight: Nullable<string>) => void;
	highlightedGroup: Nullable<string>;
	cellWidths: CellWidthsMap;
	hasPreviouslyBeenSplit: boolean;
	isOfCurrentlyHighlightedGroupKey: boolean;
	isAlternativeOption: boolean;
	workSummaryDetailIds: number[];
	belongsToProject: boolean;
	subJobs: SiblingSubjobVM[];
	handleJobReallocation: (subJob: SiblingSubjobVM, workSummaryDetailIds: number[]) => Promise<void>;
	fieldReportId: number;
	workOrderId: number;
	handleBulkBillingStatusToggle: (workSummaryDetailIds: number[]) => void;
	billingType: WorkSummaryBillingType;
}

export interface BillableWorkTableRowProps {
	isBillable: true;
	billingType: WorkSummaryBillingType.BILLABLE_WORK;
	onBillingCodeSelect: (billingCode: Nullable<BillingCodeVM>, workSummaryDetailIds: number[]) => Promise<void>;
}

export interface MaterialCostTableRowProps {
	isBillable: true;
	billingType: WorkSummaryBillingType.MATERIAL_COST;
	onBillingCodeSelect?: never;
}

export interface NonBillableWorkTableRowProps {
	isBillable: false;
	billingType: WorkSummaryBillingType.BILLABLE_WORK;
	onBillingCodeSelect?: never;
}

export interface NonBillableMaterialCostRowProps {
	isBillable: false;
	billingType: WorkSummaryBillingType.MATERIAL_COST;
	onBillingCodeSelect?: never;
}

type PropsWithoutConnector = OwnProps & (
	BillableWorkTableRowProps
	| NonBillableWorkTableRowProps
	| MaterialCostTableRowProps
	| NonBillableMaterialCostRowProps
);
type Props = PropsWithoutConnector & ConnectedProps<typeof connector>;

type DropdownOption = {
	id: number;
	customerId: string;
	unitPrice: Nullable<string>;
	description: string;
	groupName: Nullable<string>;
	groupColor: Nullable<ColorPalette>;
};

type DropdownOptionWithRowData = DropdownOption & {
	unit: Nullable<string>;
	rowUnit: Nullable<string>;
	rowQuantity: Nullable<number>;
};

const _delimitersRegEx = new RegExp('\/|-| ');

const _filterBillingCodes = (_option: BillingCodeVM, _searchText: string) => {
	_searchText.trim();
	const splitSearch = _searchText.toLowerCase().split(_delimitersRegEx).join('');
	const _customerIdToLowerCase = _option.customerId.toLowerCase().split(_delimitersRegEx).join('');
	const _descriptionToLowerCase = _option.description.toLowerCase().split(_delimitersRegEx).join('');

	if (!!splitSearch && _customerIdToLowerCase.includes(splitSearch) || _descriptionToLowerCase.includes(splitSearch)) {
		return true;
	}

	return false;
};

const _filterSubJobs = (option: SiblingSubjobVM, _searchText: string) => {
	return option.jobCode.toLowerCase().includes(_searchText.toLowerCase());
};

const _renderBillingCodeMenuItem = (_bc: DropdownOptionWithRowData) => {
	const convertedUnit = _bc.rowQuantity && _bc.rowUnit && _bc.unit
		? convertUnits(_bc.rowQuantity, QuantityUnitMap[_bc.rowUnit], QuantityUnitMap[_bc.unit]) : null;
	const convertedUnitLabel = convertedUnit && `${_bc.rowQuantity} ${_bc.rowUnit} = ${parseFloat(Number(convertedUnit).toFixed(4)).toString()} ${_bc.unit}`;
	const perUnitPriceLabel = `$ ${_bc.unitPrice} per ${_bc.unit} `;

	return (
		<div className={bemElement('field-report__work-summary__table__row__data__cell', 'dropdown-menu-item')}>
			<b>{_bc.customerId}</b>
			<span><b>{perUnitPriceLabel}{_bc.groupColor && <ColorSquare color={_bc.groupColor} isSmall={true} />}{_bc.groupName ?? ''}</b></span>
			{convertedUnit && (_bc.unit !== _bc.rowUnit)
				? <p className={styles.dropdown_card_subtitle}>{convertedUnitLabel}</p>
				: ''
			}
			<p>{_bc.description}</p>
		</div>
	);
};

const _renderNoBillingCodeMenuItem = (item: { disabled: true; label: string; }) => {
	return (
		<div className={bemElement('field-report__work-summary__table__row__data__cell', 'dropdown-menu-item')}>
			<p>
				{item.label}
			</p>
		</div>
	);
};

const _renderSubJobMenuItem = (subJob: SiblingSubjobVM) => {
	return (
		<div className={bemElement('field-report__work-summary__table__row__data__cell', 'dropdown-menu-item')}>
			<b>{subJob.jobCode}</b>
		</div>
	);
};

const _renderSelectedSubJobMenuItem = (subJob: SiblingSubjobVM) => <span>{subJob.jobCode}</span>;

const _renderSelectedDropdownMenuItem = (_billingCode: DropdownOption) => <span>{_billingCode.customerId}</span>;

const _renderSelectedDropdownMenuItemWithNoBillingCodes = ({ label }: { label: string; }) => <span>{label}</span>;

const _resolveTableCellClassName = (modifiers?) => {
	return bemElement('field-report__work-summary__table__row__data', 'cell', { ...modifiers });
};

const _fillWithEmptyFields = (fields: WorkSummaryDefinitionFieldVM[], maxNumberOfFields: number) => {
	const noOfExtraEmptyFields = maxNumberOfFields - (fields.length);
	let modifiedFields: Nullable<WorkSummaryDefinitionFieldVM>[] = [];

	if (noOfExtraEmptyFields) {
		const extraFields = new Array(noOfExtraEmptyFields).fill(null, 0, maxNumberOfFields);
		modifiedFields =
			fields
				? fields.concat(extraFields)
				: extraFields;
	} else {
		modifiedFields = fields;
	}

	return modifiedFields;
};

const BillableAndNonBillableWorkTableRow: React.FC<Props> = (props) => {
	const {
		billingCodes,
		rowData,
		maxNumberOfDefinitionFields,
		isBillable,
		onBillingCodeSelect,
		onRowHighlight,
		highlightedGroup,
		cellWidths,
		isBillableStatusEditable,
		isReadOnly,
		hasPreviouslyBeenSplit,
		isOfCurrentlyHighlightedGroupKey,
		isAlternativeOption,
		workSummaryDetailIds,
		belongsToProject,
		subJobs,
		handleJobReallocation,
		hasPermissionsToManageBillingCodes,
		updateBillingCodes,
		fieldReportId,
		workOrderId,
		handleBulkBillingStatusToggle,
		billingType,
	} = props;

	const {
		subJobId,
		billableWorkId,
		quantity,
		unit,
		work,
		type,
		definitionFields,
		billingCodeId,
		billingCodeUnit,
		unitPrice,
		total,
		description,
		customerId,
		billingGroup,
		typeFieldName,
		typeFieldType,
		workSummaryGroup,
		unitMaterialCost,
		materialCost,
		materialDisplayName,
		materialCostSource,
		materialUnit,
	} = rowData;

	const {
		value: showEditBillingCodesModal,
		setToTrue: openEditBillingCodesModal,
		setToFalse: hideEditBillingCodesModal,
	} = useToggle(false);

	const [billingCodeErrors, setBillingCodeErrors] = React.useState<Nullable<BackendBillingCodeErrors>>(null);
	const [isBillingStatusToggleDisabled, setIsBillingStatusToggleDisabled] = React.useState(false);
	const [pendingBillingCodeOption, setPendingBillingCodeOption] = React.useState<Nullable<DropdownOption>>(null);

	const groupKey = React.useMemo(() => resolveHighlightGroupKey({
		billingType,
		subJobId,
		billableWorkId,
		workType: type,
		definitionFields,
	}), [billableWorkId, billingType, definitionFields, subJobId, type]);

	const groupToHighlight = workSummaryGroup ?? groupKey;

	const onBillingCodeChange = React.useCallback(async (value: BillingCodeVM) => {
		const selectedOption: DropdownOption = {
			customerId: value.customerId,
			id: value.id,
			description: value.description,
			unitPrice: value.unitPrice,
			groupName: value.groupName,
			groupColor: value.groupColor,
		};

		setPendingBillingCodeOption(selectedOption);
		onRowHighlight(groupToHighlight, groupKey);
		await onBillingCodeSelect?.(value, workSummaryDetailIds);
	}, [groupKey, groupToHighlight, onBillingCodeSelect, onRowHighlight, workSummaryDetailIds]);

	const onBillingCodeClear = React.useCallback(() => {
		setPendingBillingCodeOption(null);
		onRowHighlight(groupToHighlight, groupKey);
		onBillingCodeSelect?.(null, workSummaryDetailIds);
	}, [groupKey, groupToHighlight, onBillingCodeSelect, onRowHighlight, workSummaryDetailIds]);

	React.useEffect(() => {
		if (!pendingBillingCodeOption) {
			return;
		}

		if (billingCodeId === pendingBillingCodeOption.id) {
			setPendingBillingCodeOption(null);
		}
	}, [billingCodeId, pendingBillingCodeOption]);

	const onSubJobChange = React.useCallback(async (subJob: SiblingSubjobVM) => {
		await handleJobReallocation?.(subJob, workSummaryDetailIds);
	}, [handleJobReallocation, workSummaryDetailIds]);

	const cellClassName = bemElement('field-report__work-summary__table__row__data', 'cell');
	const cellTextClassName = bemElement(cellClassName, 'text');

	const _definitionFieldsMapper = React.useCallback((_df: WorkSummaryDefinitionFieldVM, _index: number) => {
		const column = createIndexedColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.DEFINITION_FIELD, _index);
		const resolvedUnit = _df?.fieldUnit ? resolveToLargerUnit(_df.fieldUnit) : '';

		return (
			<span
				className={_resolveTableCellClassName()}
				key={_index}
				style={resolveStyleForColumn(column, cellWidths)}
			>
				{
					_df ? (
						<span className={cellTextClassName}>
							{_df.fieldName}: {resolveFieldValue(_df.value, _df.fieldType, _df.fieldUnit)} {resolvedUnit}
						</span>
					) :
						<span className={cellTextClassName}>
							N/A
						</span>
				}
			</span >
		);
	}, [cellTextClassName, cellWidths]);

	const filteredBillingCodeOptions = React.useMemo(
		() => billingCodes.reduce<DropdownOptionWithRowData[]>((_acc: DropdownOptionWithRowData[], _bc: BillingCodeVM) => {
			if ((
				!unit
				|| (UnitClasses[QuantityUnitMap[_bc.unit]] && (UnitClasses[QuantityUnitMap[_bc.unit]] === UnitClasses[unit]) // if there is a defined conversion rate
					|| (QuantityUnitMap[_bc.unit] === unit)) // if units are the same
			) && !_bc.deletedAt
			) {
				_acc.push({
					customerId: _bc.customerId,
					id: _bc.id,
					description: _bc.description,
					unitPrice: _bc.unitPrice,
					groupName: _bc.groupName,
					unit: _bc.unit,
					rowUnit: rowData.unit,
					rowQuantity: rowData.quantity,
					groupColor: _bc.groupColor,
				});
			}
			return _acc;
		}, [])
		, [billingCodes, unit, rowData]);

	const selectedBillingCode = React.useMemo(
		() => billingCodes.find((_bc) => _bc.id === billingCodeId, {}),
		[billingCodeId, billingCodes]
	);

	const selectedBillingCodeOption = React.useMemo<Nullable<DropdownOption>>(() => {
		if (selectedBillingCode) {
			return {
				customerId: selectedBillingCode.customerId,
				id: selectedBillingCode.id,
				description: selectedBillingCode.description,
				unitPrice: selectedBillingCode.unitPrice,
				groupName: selectedBillingCode.groupName,
				unit: null,
				rowUnit: null,
				rowQuantity: 0,
				groupColor: selectedBillingCode.groupColor,
			};
		}
		if (customerId && billingCodeId && description) {
			const color = billingCodes.find((_bc) => _bc.id === billingCodeId)?.groupColor;
			return {
				customerId,
				id: billingCodeId,
				unitPrice: unitPrice ? unitPrice + '' : '',
				description,
				groupName: billingGroup,
				unit: null,
				rowUnit: null,
				rowQuantity: 0,
				groupColor: color ?? null,
			};
		}

		return null;
	}, [billingCodeId, billingCodes, billingGroup, customerId, description, selectedBillingCode, unitPrice]);

	const resolvedSelectedBillingCodeOption = React.useMemo(() => pendingBillingCodeOption ?? selectedBillingCodeOption, [
		pendingBillingCodeOption,
		selectedBillingCodeOption,
	]);

	const hasSelectableBillingCodes = filteredBillingCodeOptions.length > 0;

	const onRowClick = React.useCallback(() => onRowHighlight(groupToHighlight, groupKey), [groupKey, groupToHighlight, onRowHighlight]);

	const _renderDropdown = React.useCallback(() => {

		const addOrEditBCOption: {
			hasActionOption: true;
			actionText: string;
			onActionClick: () => void;
		} = { hasActionOption: true, actionText: 'Add or Edit Billing Codes', onActionClick: openEditBillingCodesModal };
		if (!hasSelectableBillingCodes) {
			return (
				<Dropdown
					className={bemElement('field-report__work-summary__table__row__data__cell', 'dropdown')}
					defaultValue={customerId ? { label: customerId } : undefined}
					onClear={onBillingCodeClear}
					options={[{ disabled: true, label: 'No billing codes found.' }]}
					placeholder="No billing codes found."
					renderMenuItem={_renderNoBillingCodeMenuItem}
					renderSelected={_renderSelectedDropdownMenuItemWithNoBillingCodes}
					withBorder={true}
					withCaret={true}
					{...(hasPermissionsToManageBillingCodes ? addOrEditBCOption : { hasOpenModalOption: false })}
				/>
			);
		}

		return (
			<Dropdown<DropdownOption>
				className={bemElement('field-report__work-summary__table__row__data__cell', 'dropdown')}
				defaultValue={resolvedSelectedBillingCodeOption}
				disabled={isReadOnly}
				filterable={true}
				filterBy={_filterBillingCodes}
				labelKey="customerId"
				onClear={onBillingCodeClear}
				onValueChange={onBillingCodeChange}
				options={filteredBillingCodeOptions}
				placeholder="Add Billing Code"
				renderMenuItem={_renderBillingCodeMenuItem}
				renderSelected={_renderSelectedDropdownMenuItem}
				valueKey="id"
				withBorder={true}
				withCaret={true}
				{...(hasPermissionsToManageBillingCodes ? addOrEditBCOption : { hasOpenModalOption: false })}
			/>);
	}, [
		hasSelectableBillingCodes,
		openEditBillingCodesModal,
		isReadOnly,
		onBillingCodeClear,
		onBillingCodeChange,
		filteredBillingCodeOptions,
		hasPermissionsToManageBillingCodes,
		customerId,
		resolvedSelectedBillingCodeOption,
	]);

	const renderBillingCode = React.useCallback(() => {
		if (isBillable) {
			return (
				<span
					className={_resolveTableCellClassName({ long: true, dropdown: true })}
					style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.CHARGE_RATE, cellWidths)}
				>
					{isReadOnly
						? <span className={cellTextClassName}>
							{customerId}
						</span>
						: _renderDropdown()
					}
				</span>
			);
		}

		return (
			<span
				className={_resolveTableCellClassName({ long: true, dropdown: true })}
				style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.BILLING_CODE, cellWidths)}
			>
				<span className={cellTextClassName}>
					{customerId}
				</span>
			</span>
		);

	}, [_renderDropdown, cellTextClassName, cellWidths, customerId, isBillable, isReadOnly]);

	const renderMaterialCost = React.useCallback(() => {
		if (isBillable && !isReadOnly) {
			return (
				<span
					className={_resolveTableCellClassName({ long: true, dropdown: true })}
					style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.MATERIAL, cellWidths)}
				>
					<MaterialCostDropdown
						billableWorkId={billableWorkId}
						fieldReportId={fieldReportId}
						readonly={isReadOnly}
						selectedMaterialCostSource={materialCostSource}
						selectedMaterialName={materialDisplayName}
						subJobId={subJobId}
						unit={unit as Nullable<QuantityUnitType>}
						workSummaryDetailIds={workSummaryDetailIds}
					/>
				</span>
			);
		}

		return (
			<span
				className={_resolveTableCellClassName({ long: true, dropdown: true })}
				style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.MATERIAL_NAME, cellWidths)}
			>
				<span className={`${cellTextClassName} ${styles.materialNameCell}`}>
					{materialCostSource && <MaterialRateSource rateSource={materialCostSource} />}
					<span className={styles.materialName}>{materialDisplayName ?? ''}</span>
				</span>
			</span>
		);
	}, [
		billableWorkId,
		cellTextClassName,
		cellWidths,
		fieldReportId,
		isBillable,
		isReadOnly,
		materialCostSource,
		materialDisplayName,
		subJobId,
		workSummaryDetailIds,
		unit,
	]);

	const renderUnitPrice = React.useCallback(() => {
		if (!unit || !unitPrice) {
			return '';
		}
		const convertedUnitPrice = billingCodeUnit && unit !== billingCodeUnit
			? convertUnits(unitPrice, QuantityUnitMap[unit], QuantityUnitMap[billingCodeUnit])
			: null;

		const tooltipMessage = convertedUnitPrice
			? <>
				<div>Quantity tracked in {unit}</div>
				<div>Billing code in {billingCodeUnit}</div>
			</>
			: null;

		const renderValue = convertedUnitPrice
			? `$${parseFloat(convertedUnitPrice.toFixed(8))} per ${unit} ($${parseFloat(unitPrice.toFixed(4))} per ${billingCodeUnit})`
			: `$${parseFloat(unitPrice.toFixed(4))} per ${unit}`;

		return tooltipMessage
			? <Tooltip message={tooltipMessage}><span className={bemBlock(cellTextClassName, { 'align-right': true })}>{renderValue}</span></Tooltip>
			: renderValue;
	}, [unit, billingCodeUnit, unitPrice, cellTextClassName]);

	const { selectedSubJob, selectableSubJobs } = React.useMemo(() => {
		return (subJobs ?? []).reduce<{ selectedSubJob: Nullable<SiblingSubjobVM>; selectableSubJobs: SiblingSubjobVM[]; }>((_acc, _subJob) => {
			if (_subJob.id === subJobId) {
				_acc.selectedSubJob = _subJob;
			} else {
				_acc.selectableSubJobs.push(_subJob);
			}
			return _acc;
		}, { selectedSubJob: null, selectableSubJobs: [] });
	}, [subJobId, subJobs]);

	const renderReallocationDropdown = React.useCallback(() => {
		if (!subJobs?.length) {
			return null;
		}
		return (
			<Dropdown<SiblingSubjobVM>
				className={bemElement('field-report__work-summary__table__row__data__cell', 'dropdown')}
				defaultValue={selectedSubJob}
				disabled={isReadOnly}
				filterable={true}
				filterBy={_filterSubJobs}
				labelKey="jobCode"
				onValueChange={onSubJobChange}
				options={selectableSubJobs}
				placeholder="Reallocate"
				renderMenuItem={_renderSubJobMenuItem}
				renderSelected={_renderSelectedSubJobMenuItem}
				valueKey="id"
				withBorder={true}
				withCaret={true}
			/>
		);
	}, [isReadOnly, onSubJobChange, selectableSubJobs, selectedSubJob, subJobs?.length]);

	const onHandleBulkBillingStatusToggle = React.useCallback(() => {
		setIsBillingStatusToggleDisabled(true);
		handleBulkBillingStatusToggle(workSummaryDetailIds);
		setIsBillingStatusToggleDisabled(false);
	}, [handleBulkBillingStatusToggle, workSummaryDetailIds]);

	const onEditBillingCodes = React.useCallback(async (editedBillingCodes: W_BillingCode_UpdateBillingCode_RM[]) => {
		if (hasPermissionsToManageBillingCodes) {
			const wrId = selectedSubJob?.id ?? subJobId;
			const result = await updateBillingCodes({ billingCodes: editedBillingCodes, fieldReportId, workOrderId }, wrId);
			if (!result.success) {
				if (result.details) {
					setBillingCodeErrors(result.details);
				}
				return;
			}
		}
		setBillingCodeErrors(null);
		hideEditBillingCodesModal();
	}, [hasPermissionsToManageBillingCodes, hideEditBillingCodesModal, selectedSubJob?.id, subJobId, updateBillingCodes, fieldReportId, workOrderId]);

	const closeEditBillingCodesModal = React.useCallback(() => {
		hideEditBillingCodesModal();
		setBillingCodeErrors(null);
	}, [hideEditBillingCodesModal]);

	const modifiedDefinitionFields = _fillWithEmptyFields(definitionFields, maxNumberOfDefinitionFields);
	const isBulkEditStatusDisabled = !isBillableStatusEditable || isBillingStatusToggleDisabled;
	const isHighlighted = (highlightedGroup === groupToHighlight);
	const indicatePreviousSplit = hasPreviouslyBeenSplit && isOfCurrentlyHighlightedGroupKey;
	const billingStatusClassName = bemElement(cellClassName, 'billing-action', { billed: isBillable, readonly: isBulkEditStatusDisabled });

	const rowClassName = bemElement('field-report__work-summary__table', 'row__data', {
		highlighted: isHighlighted,
		split: indicatePreviousSplit,
		'billable-table': true,
		alternative: isAlternativeOption,
	});

	let workTypeAdjustedName = <>{typeFieldName}</>;
	if (
		typeFieldType !== ReportBlockFieldEnum.Type.NUMERIC_ATTRIBUTE
		&& typeFieldType !== ReportBlockFieldEnum.Type.CALCULATED
	) {
		const resolvedValue = resolveFieldValue(type, typeFieldType, null);
		workTypeAdjustedName =
			<>
				{typeFieldName}: &nbsp;{resolvedValue}
			</>;
	}

	const isBillableWork = billingType === WorkSummaryBillingType.BILLABLE_WORK;
	const isMaterialCost = billingType === WorkSummaryBillingType.MATERIAL_COST;

	return (
		<>
			<div
				className={rowClassName}
				onClick={onRowClick}
			>
				<span className={_resolveTableCellClassName({ actions: true })}>
					<span className={billingStatusClassName} onClick={!isBulkEditStatusDisabled ? onHandleBulkBillingStatusToggle : undefined}>
						<span className="icon-dollar" />
					</span>
				</span>
				<span
					className={_resolveTableCellClassName()}
					style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.WORK, cellWidths)}
				>
					<span className={cellTextClassName}>
						{work}
					</span>
				</span>
				<span
					className={_resolveTableCellClassName()}
					style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.TYPE, cellWidths)}
				>
					<span className={cellTextClassName}>
						{workTypeAdjustedName}
					</span>
				</span>
				{modifiedDefinitionFields?.map(_definitionFieldsMapper)}
				<span
					className={_resolveTableCellClassName({ short: true })}
					style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.QUANTITY, cellWidths)}
				>
					<span className={bemBlock(cellTextClassName, { 'align-right': true })}>
						{quantity}
					</span>
				</span>
				<span
					className={_resolveTableCellClassName({ short: true })}
					style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.UNIT, cellWidths)}
				>
					<span className={cellTextClassName}>
						{(unit && QuantityUnitMap[unit]) ? QuantityUnitMap[unit] : 'N/A'}
					</span>
				</span>
				{isBillableWork && (
					<>
						{renderBillingCode()}
						<span
							className={_resolveTableCellClassName()}
							style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.UNIT_PRICE, cellWidths)}
						>
							<span className={bemBlock(cellTextClassName, { 'align-right': true })}>
								{renderUnitPrice()}
							</span>
						</span>
						<span
							className={_resolveTableCellClassName({ long: true })}
							style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.DESCRIPTION, cellWidths)}
						>
							<span className={cellTextClassName}>
								{description}
							</span>
						</span>
						<span
							className={_resolveTableCellClassName()}
							style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.REVENUE, cellWidths)}
						>
							<span className={bemBlock(cellTextClassName, { 'align-right': true })}>
								{total !== null ? dollarFormatter.format(total) : ''}
							</span>
						</span>
					</>
				)}
				{isMaterialCost && (
					<>
						{renderMaterialCost()}
						<span
							className={_resolveTableCellClassName()}
							style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.MATERIAL_TOTAL_COST, cellWidths)}
						>
							<span className={bemBlock(cellTextClassName, { 'align-right': true })}>
								<ConvertedMaterialCost
									materialCost={unitMaterialCost}
									materialUnit={materialUnit}
									quantityUnit={unit as QuantityUnitType}
								/>
							</span>
						</span>
						<span
							className={_resolveTableCellClassName({ long: true })}
							style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.DESCRIPTION, cellWidths)}
						>
							<span className={cellTextClassName}>
								{description}
							</span>
						</span>
						<span
							className={_resolveTableCellClassName()}
							style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.MATERIAL_AMOUNT, cellWidths)}
						>
							<span className={bemBlock(cellTextClassName, { 'align-right': true })}>
								{materialCost !== null ? dollarFormatter.format(materialCost) : ''}
							</span>
						</span>
					</>
				)}
				{belongsToProject && (
					<span
						className={_resolveTableCellClassName({ long: true, dropdown: true })}
						style={resolveStyleForColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.REALLOCATE, cellWidths)}
					>
						{renderReallocationDropdown()}
					</span>
				)}
			</div>
			{
				showEditBillingCodesModal &&
				<EditBillingCodesModal
					billingCodes={billingCodes}
					closeModal={closeEditBillingCodesModal}
					errors={billingCodeErrors}
					onSubmit={onEditBillingCodes}
					showModal={showEditBillingCodesModal}
					workRequestId={selectedSubJob?.id ?? subJobId}
				/>
			}
		</>
	);
};

function mapStateToProps(state: RootState) {
	const { user: { companyData, userData } } = state;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const { isCompanyAdmin, permissions } = companyData;
	const { role } = userData;

	const hasPermissionsToManageBillingCodes = isAllowed(
		PagePermissions.COMPANY.JOBS.MANAGE_BILLING_CODES,
		permissions,
		isCompanyAdmin,
		role
	);

	return {
		hasPermissionsToManageBillingCodes,
	};
}

function mapDispatchToProps() {
	return {
		updateBillingCodes: WorkRequestActions.updateBillingCodes,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<PropsWithoutConnector>>(
	React.memo,
	connector
);

export default enhance(BillableAndNonBillableWorkTableRow);
